import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const coreValues = [
  {
    title: "Client-Centered Care",
    description: "Keeping our clients' quality of life and well-being central in the design and delivery of services.",
    icon: "ri-heart-3-line",
  },
  {
    title: "Respect & Dignity",
    description: "Treating every client with respect, dignity, compassion, empathy, honesty, and integrity while maintaining confidentiality.",
    icon: "ri-user-received-line",
  },
  {
    title: "Inclusive Service",
    description: "Showing respect for all cultures, religions, ethnicities, sexual orientations, ages, gender, and disabilities.",
    icon: "ri-globe-line",
  },
  {
    title: "Staff Excellence",
    description: "Recruiting, training, and retraining competent staff who are our greatest asset.",
    icon: "ri-award-line",
  },
  {
    title: "Workplace Support",
    description: "Valuing, supporting, recognizing, and appreciating our staff through a nurturing work environment.",
    icon: "ri-team-line",
  },
  {
    title: "Community Partnership",
    description: "Developing and maintaining positive relationships with the community and local organizations.",
    icon: "ri-hand-heart-line",
  },
  {
    title: "Accountability",
    description: "Conducting our business in an accountable and responsible manner with continuous quality improvement.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Ethical Standards",
    description: "Adhering to the professional code of ethics of the home care industry in all we do.",
    icon: "ri-scales-3-line",
  },
];

export default function About() {
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const observerRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-animate-id");
          if (id && entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    observerRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLElement | null) => {
    if (el) observerRefs.current.set(id, el);
  };

  const animateClass = (id: string, base = "") =>
    `transition-all duration-700 ease-out ${base} ${
      isVisible[id] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[420px] flex items-center justify-center overflow-hidden bg-brand-green-800">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract soft green and beige organic shapes pattern, subtle texture, warm neutral tones, calming home care background, minimal and elegant, no text, gentle gradients"
            alt=""
            className="w-full h-full object-cover"
            width="1600"
            height="500"
          />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-300 mb-3 block">
            About Our Company
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
            Rooted in Family, Growing with Purpose
          </h1>
          <p className="text-sm md:text-base text-brand-green-200 max-w-2xl mx-auto leading-relaxed">
            Sycamore Oasis was born from a deeply personal journey — one that opened our eyes to the life-changing power of compassionate, consistent home care.
          </p>
        </div>
      </section>

      {/* Our Story — Founder Narrative */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="story" ref={setRef("story")}>
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto">
          <div className={`${animateClass("story")} text-center`}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Our Story
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              A Journey That Changed Everything
            </h2>

            <div className="space-y-5 text-sm md:text-base text-gray-600 leading-relaxed max-w-3xl mx-auto text-left">
              <p>
                I lost my father in 2015, and after his passing, my mother's emotional health began to decline. Every time I received a call from home, my heart would race, bracing myself for bad news. I have four siblings, and one of my sisters often shared how poorly she was being treated at work and how little she was being paid.
              </p>
              <p>
                That's when I asked if she would be willing to care for our mother, and I promised to compensate her. She agreed.
              </p>
              <p>
                <span className="font-semibold text-brand-green-700">That decision changed everything.</span> My sister providing care for our mother at home brought stability, comfort, and renewed joy into her life. While my mother is elderly, she is truly thriving.
              </p>
              <p>
                Seeing the difference that compassionate, consistent care made in our own family opened our eyes. My husband and I recognized a need, and we felt called to do something about it. That calling became our commitment — to provide excellent, reliable home care services that allow individuals to remain safe, comfortable, and happy in their own homes.
              </p>
              <p className="text-brand-green-700 font-medium">
                Our greatest reward is seeing our clients content, supported, and flourishing under our care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Name / Mission Block */}
      <section className="py-14 md:py-20 bg-warm-beige" data-animate-id="name" ref={setRef("name")}>
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className={animateClass("name")}>
            <div className="w-12 h-12 rounded-full bg-brand-green-100 flex items-center justify-center mx-auto mb-6">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-tree-line text-xl text-brand-green-600" />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why We Are Called Sycamore Oasis
            </h2>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-6">
              Just as the Sycamore tree offers shade, an oasis provides rest after a long and weary journey. At Sycamore Oasis, our mission is to offer that same sense of comfort and renewal. We aim to ease the burdens of daily life by making each day more manageable and peaceful.
            </p>
            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
              Let us help transform your surroundings into a place of rest by delivering quality care and thoughtful attention.
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision & Our Business */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="mission" ref={setRef("mission")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("mission", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              Who We Are
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Mission, Vision & Our Business
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Mission */}
            <div className={animateClass("mission")}>
              <div className="bg-brand-green-50 rounded-2xl p-6 md:p-8 h-full">
                <div className="w-10 h-10 rounded-lg bg-brand-green-100 flex items-center justify-center mb-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-focus-3-line text-brand-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Mission</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  We are committed to providing high quality, client-centered and affordable Home Care Services to our clients to assist them to lead dignified and independent lives in the comfort and safety of their own homes. Their individual needs will be carefully assessed, understood and met through the selective assignments to qualified, trustworthy and compassionate personnel.
                </p>
              </div>
            </div>

            {/* Vision */}
            <div className={animateClass("mission")}>
              <div className="bg-warm-beige rounded-2xl p-6 md:p-8 h-full">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center mb-4">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-line text-brand-green-600" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Our Vision</h3>
                <ul className="space-y-2 text-sm text-gray-600 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-brand-green-600 text-xs" />
                    </div>
                    To be known and valued for providing the highest standard of in-home care services.
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-brand-green-600 text-xs" />
                    </div>
                    To be the provider of choice in the community.
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-brand-green-600 text-xs" />
                    </div>
                    To be the employer of choice in the community.
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-brand-green-600 text-xs" />
                    </div>
                    To be a financially viable agency.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Our Business */}
          <div className={animateClass("mission", "mt-8 lg:mt-12")}>
            <div className="bg-soft-gray rounded-2xl p-6 md:p-8 border border-gray-100">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green-50 flex items-center justify-center shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-building-line text-brand-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Our Business</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    Sycamore Oasis is licensed through the Illinois Department of Public Health in Home Care services, and offers excellent homemaker services to our numerous clients, to assist them in their activities of daily living. We are committed to providing our clients with excellent home services and care. Our delight is seeing our clients happy and satisfied being in our care.
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Just like the Sycamore tree, we offer a shade from life's elements and a respite by the oasis after a long and weary journey. To our clients, we represent both a shade and a place of rest that help ease the burden of everyday life. This we accomplish by providing quality care and attention for our clients. Our aim is to make life a bit easier and less stressful for our clients, thereby enhancing their quality of life and providing them with peace of mind.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-14 md:py-20 bg-soft-gray" data-animate-id="values" ref={setRef("values")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("values", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              What Drives Us
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              Our Core Values
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Our mission and vision are achieved through the application of these core values.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {coreValues.map((value, i) => (
              <div
                key={value.title}
                className={`bg-white rounded-xl p-6 border border-gray-100 hover:border-brand-green-200 hover:shadow-md transition-all duration-300 ${
                  isVisible["values"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${value.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We're Different */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="different" ref={setRef("different")}>
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto">
          <div className={animateClass("different", "text-center mb-10 md:mb-14")}>
            <span className="text-xs font-semibold uppercase tracking-widest text-brand-green-600 mb-2 block">
              What Sets Us Apart
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">
              How We Are Different
            </h2>
            <p className="text-sm md:text-base text-gray-500 max-w-2xl mx-auto">
              Six things that make Sycamore Oasis unlike any other home care agency in Illinois.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                icon: "ri-user-settings-line",
                title: "Personalized Care, Just for You",
                description: "We do not believe in a one-size-fits-all approach. Every client's needs are unique. We take the time to understand your goals and challenges, and we customize care plans that align with what matters most to you.",
              },
              {
                icon: "ri-repeat-one-line",
                title: "Consistency You Can Trust",
                description: "Trust is built through familiarity and reliability. That is why we ensure the same caregivers attend to our clients on every shift, delivering consistent, compassionate care you can count on.",
              },
              {
                icon: "ri-hand-coin-line",
                title: "Care Tailored to Your Budget",
                description: "Everyone deserves quality care that feels like home. Our rate guarantee and flexible financial options are designed to keep your care both exceptional and worthwhile for the services we provide.",
              },
              {
                icon: "ri-group-line",
                title: "We Hire Family Members",
                description: "We believe family can be the heart of great care. That is why we welcome family members to join our caregiving team using the same rigorous process as traditional caregivers — background checks, fingerprinting, mandatory training, and reference verification.",
              },
              {
                icon: "ri-home-heart-line",
                title: "A Caring, Family-Like Workplace",
                description: "We have cultivated a friendly, supportive environment where caregivers feel valued and heard. Our open-door policy and warm workplace culture shine through our low employee turnover.",
              },
              {
                icon: "ri-time-line",
                title: "Full Shift Coverage Guaranteed",
                description: "If a caregiver calls off sick or for an emergency, we quickly find a replacement to ensure continuous care for our clients. You never have to worry about gaps in coverage.",
              },
            ].map((item, i) => (
              <div
                key={item.title}
                className={`bg-soft-gray rounded-xl p-5 md:p-6 border border-gray-100 hover:border-brand-green-200 hover:shadow-md hover:-translate-y-1 transition-all duration-300 ${
                  isVisible["different"] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-green-50 flex items-center justify-center mb-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-brand-green-600`} />
                  </div>
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hours of Operation */}
      <section className="py-14 md:py-20 bg-white" data-animate-id="hours" ref={setRef("hours")}>
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          <div className={animateClass("hours", "grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12")}>
            <div className="bg-brand-green-50 rounded-2xl p-6 md:p-8">
              <div className="w-10 h-10 rounded-lg bg-brand-green-100 flex items-center justify-center mb-4">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-brand-green-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Hours of Operation</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium text-gray-900">Monday – Friday:</span> 9:00 AM – 5:00 PM</p>
                <p><span className="font-medium text-gray-900">Weekend & After Hours:</span> 24/7 On-call Manager Available</p>
              </div>
            </div>

            <div className="bg-warm-beige rounded-2xl p-6 md:p-8">
              <div className="w-10 h-10 rounded-lg bg-white border border-gray-100 flex items-center justify-center mb-4">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-brand-green-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Office Address</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <p className="font-medium text-gray-900">Sycamore Oasis Corporation</p>
                <p>4055 S Route 59, Suite 121</p>
                <p>Naperville, IL 60504</p>
                <p className="mt-2"><span className="font-medium">Phone:</span> 773-699-7894</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-brand-green-800">
        <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
          <div className="w-12 h-12 rounded-full bg-brand-green-700 flex items-center justify-center mx-auto mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-heart-3-line text-2xl text-brand-green-300" />
            </div>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Join the Sycamore Oasis Family
          </h2>
          <p className="text-sm md:text-base text-brand-green-200 mb-8 max-w-2xl mx-auto leading-relaxed">
            Whether you are seeking care for a loved one or looking for a meaningful career in caregiving, we would love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/contact"
              className="w-full sm:w-auto px-8 py-3.5 bg-white text-brand-green-800 font-semibold rounded-full hover:bg-brand-green-50 transition-all duration-200 text-center whitespace-nowrap"
            >
              Get Care Now
            </Link>
            <Link
              to="/careers"
              className="w-full sm:w-auto px-8 py-3.5 bg-brand-green-700 text-white font-semibold rounded-full hover:bg-brand-green-600 transition-all duration-200 text-center whitespace-nowrap"
            >
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}